import { useState } from 'react';
import { CalendarBlank, Camera } from '@phosphor-icons/react';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card';
import { notificationsAPI } from '../../data/notifications';
import pt from '../../locales/pt';

function ToggleRow({ icon: Icon, title, description, checked, onChange, disabled }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-4 last:border-0">
      <div className="flex gap-3">
        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-sage-600" aria-hidden />
        <div>
          <p className="font-medium text-foreground">{title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <label className="relative inline-flex shrink-0 cursor-pointer items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="peer h-6 w-11 rounded-full bg-stone-300 dark:bg-stone-600 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-sage-600 peer-checked:after:translate-x-full peer-disabled:opacity-50" />
      </label>
    </div>
  );
}

export function Reminders() {
  const profile = useLoaderData();
  const { revalidate } = useRevalidator();
  const [saving, setSaving] = useState(false);

  const onToggle = async (field, value) => {
    setSaving(true);
    const res = await notificationsAPI.updatePreferences({ [field]: value });
    setSaving(false);
    if (res.success) {
      toast.success(pt.settings.reminders.saved);
      revalidate();
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 md:p-8">
      <div className="hidden md:block">
        <h1 className="text-2xl font-semibold text-foreground">{pt.settings.reminders.title}</h1>
        <p className="mt-1 text-muted-foreground">{pt.settings.reminders.subtitle}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{pt.settings.reminders.cardTitle}</CardTitle>
          <CardDescription>{pt.settings.reminders.cardDesc}</CardDescription>
        </CardHeader>
        <CardContent>
          <ToggleRow
            icon={Camera}
            title={pt.settings.reminders.selfieTitle}
            description={pt.settings.reminders.selfieDesc}
            checked={profile.selfie_notifications_enabled ?? true}
            disabled={saving}
            onChange={(v) => onToggle('selfie_notifications_enabled', v)}
          />
          <ToggleRow
            icon={CalendarBlank}
            title={pt.settings.reminders.annualTitle}
            description={pt.settings.reminders.annualDesc}
            checked={profile.annual_calendar_reminders_enabled ?? true}
            disabled={saving}
            onChange={(v) => onToggle('annual_calendar_reminders_enabled', v)}
          />
        </CardContent>
      </Card>
    </div>
  );
}

Reminders.loader = async () => {
  const { notificationsLoader } = await import('../../data/notifications');
  return notificationsLoader();
};
